import { useMemo } from 'react';
import { type LineString, featureCollection } from '@turf/helpers';
import { useSelector } from '../../../redux';
import { selectCustomRouteInactiveSegments } from '../../../redux/customRoute';
import { PolylineSource } from '../../Polyline';
import { LineProperties } from '../../../types/CustomRoute';
import getLineStrings from './getLineStrings';

// Use a separate source for inactive segments so that updates to the active segment do not cause a re-render of the inactive segments.
// https://docs.mapbox.com/help/troubleshooting/mapbox-gl-js-performance/#use-a-separate-geojson-source-for-data-that-needs-to-be-updated-rapidly

export const UNIQUE_ID = 'custom-route-polylines-inactive';

const InactivePolylines = () => {
  const { activeSegmentId, hoveredSegmentId, selectedWaypointId } = useSelector(state => ({
    activeSegmentId: state.customRoute.activeSegmentId,
    hoveredSegmentId: state.customRoute.hoveredSegmentId,
    selectedWaypointId: state.customRoute.selectedWaypointId
  }));
  const opacity = selectedWaypointId !== undefined || activeSegmentId !== undefined || hoveredSegmentId !== undefined ? 0.5 : 1;

  // Use the selector for inactive segments to avoid re-rendering when the active segment changes
  const segments = useSelector(selectCustomRouteInactiveSegments);

  const data = useMemo(
    () =>
      featureCollection<LineString, LineProperties>(
        segments
          ?.filter(segment => segment.id !== activeSegmentId)
          ?.map(segment => {
            const lineStrings = getLineStrings(segment, segment.id === hoveredSegmentId ? 1 : undefined);
            return lineStrings;
          })
          .flat()
      ),
    [activeSegmentId, hoveredSegmentId, segments]
  );

  return <PolylineSource data={data} opacity={opacity} uniqueId={UNIQUE_ID} hasSourcePaint />;
};

export default InactivePolylines;
